<template>
  <v-container>
    <v-row>
      <!-- Vehicle Count -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="data-card">
          <v-card-text>
            <div class="data-card__value-title">Total Vehicles</div>
            <div>
              <div
                v-if="isLoading || isWaitingForParams"
                class="data-card__value-skeleton"
              >
                <v-skeleton-loader
                  type="chip"
                  :style="{ paddingBottom: '3px' }"
                />
              </div>
              <div v-else class="data-card__value">
                {{ totalVehicles > 0 ? totalVehicles.toLocaleString() : '0' }}
              </div>
              <div
                v-if="isComparedDataLoading"
                class="data-card__stat-skeleton"
              >
                <v-skeleton-loader type="text" />
              </div>
              <div v-else class="data-card__stat text-subtitle-2">
                <div v-if="props.previousStartDate">
                  <template v-if="comparedVehicles === 0">
                    <v-icon
                      v-show="totalVehicles > 0"
                      color="success"
                      size="small"
                      class="mr-1"
                      >mdi-arrow-up</v-icon
                    >
                    <span class="text-success" v-if="totalVehicles > 0">{{
                      totalVehicles.toLocaleString()
                    }}</span>
                    <span class="text-medium-emphasis" v-else>0</span>
                  </template>
                  <template v-else>
                    <v-icon
                      :color="vehiclesChange >= 0 ? 'success' : 'error'"
                      size="small"
                      class="mr-1"
                      v-show="comparedVehicles > 0"
                    >
                      {{
                        vehiclesChange >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down'
                      }}
                    </v-icon>
                    <v-tooltip
                      location="top"
                      :text="`Percentage change from previous period: ${vehiclesChange >= 0 ? '+' : '-'}${Math.abs(vehiclesChange).toFixed(2)}%`"
                    >
                      <template v-slot:activator="{ props }">
                        <span
                          v-bind="props"
                          :class="
                            vehiclesChange >= 0 ? 'text-success' : 'text-error'
                          "
                          style="cursor: help"
                        >
                          {{ Math.abs(vehiclesChange).toFixed(2) }}%
                        </span>
                      </template>
                    </v-tooltip>
                  </template>
                  <v-tooltip
                    location="top"
                    :text="`Previous period value: ${comparedVehicles.toLocaleString()} vehicles`"
                  >
                    <template v-slot:activator="{ props }">
                      <span
                        v-bind="props"
                        class="data-card__compared-value"
                        style="cursor: help"
                      >
                        ({{ comparedVehicles }})
                      </span>
                    </template>
                  </v-tooltip>
                </div>
                <div v-else class="text-medium-emphasis">
                  <span>Total Number of Vehicles</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- View Impressions -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="data-card">
          <v-card-text>
            <div class="data-card__value-title">Total Impressions</div>
            <div>
              <div
                v-if="isLoading || isWaitingForParams"
                class="data-card__value-skeleton"
              >
                <v-skeleton-loader type="chip" />
              </div>
              <div v-else class="data-card__value">
                {{
                  totalImpressions > 0 ? totalImpressions.toLocaleString() : '0'
                }}
              </div>
              <div
                v-if="isComparedDataLoading"
                class="data-card__stat-skeleton"
              >
                <v-skeleton-loader type="text" />
              </div>
              <div v-else class="data-card__stat text-subtitle-2">
                <div v-if="props.previousStartDate">
                  <template v-if="comparedImpressions === 0">
                    <v-icon
                      v-show="totalImpressions > 0"
                      color="success"
                      size="small"
                      class="mr-1"
                      >mdi-arrow-up</v-icon
                    >
                    <span class="text-success" v-if="totalImpressions > 0">{{
                      totalImpressions.toLocaleString()
                    }}</span>
                    <span class="text-medium-emphasis" v-else>0</span>
                  </template>
                  <template v-else>
                    <v-icon
                      :color="impressionsChange >= 0 ? 'success' : 'error'"
                      size="small"
                      class="mr-1"
                      v-show="comparedImpressions > 0"
                    >
                      {{
                        impressionsChange >= 0
                          ? 'mdi-arrow-up'
                          : 'mdi-arrow-down'
                      }}
                    </v-icon>
                    <v-tooltip
                      location="top"
                      :text="`Percentage change from previous period: ${impressionsChange >= 0 ? '+' : '-'}${Math.abs(impressionsChange).toFixed(2)}%`"
                    >
                      <template v-slot:activator="{ props }">
                        <span
                          v-bind="props"
                          :class="
                            impressionsChange >= 0
                              ? 'text-success'
                              : 'text-error'
                          "
                          style="cursor: help"
                        >
                          {{ Math.abs(impressionsChange).toFixed(2) }}%
                        </span>
                      </template>
                    </v-tooltip>
                  </template>
                  <v-tooltip
                    location="top"
                    :text="`Previous period value: ${comparedImpressions.toLocaleString()} impressions`"
                  >
                    <template v-slot:activator="{ props }">
                      <span
                        v-bind="props"
                        class="data-card__compared-value"
                        style="cursor: help"
                      >
                        ({{ comparedImpressions }})
                      </span>
                    </template>
                  </v-tooltip>
                </div>
                <div v-else class="text-medium-emphasis">
                  <span>Total Number of Times Vehicles were Viewed</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Click Count -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="data-card">
          <v-card-text>
            <div class="data-card__value-title">Total Clicks</div>
            <div>
              <div
                v-if="isLoading || isWaitingForParams"
                class="data-card__value-skeleton"
              >
                <v-skeleton-loader type="chip" />
              </div>
              <div
                v-else
                class="data-card__value"
                :style="{ paddingTop: '2px' }"
              >
                <span>{{ totalClicks }}</span>
              </div>
              <div
                v-if="isComparedDataLoading"
                class="data-card__stat-skeleton"
              >
                <v-skeleton-loader type="text" />
              </div>
              <div v-else class="data-card__stat">
                <div v-if="props.previousStartDate">
                  <template v-if="comparedClicks === 0">
                    <v-icon
                      v-show="totalClicks > 0"
                      color="success"
                      size="small"
                      class="mr-1"
                      >mdi-arrow-up</v-icon
                    >
                    <span class="text-success" v-if="totalClicks > 0">{{
                      totalClicks.toLocaleString()
                    }}</span>
                    <span class="text-medium-emphasis" v-else>0</span>
                  </template>
                  <template v-else>
                    <v-icon
                      :color="clicksChange >= 0 ? 'success' : 'error'"
                      size="small"
                      class="mr-1"
                      v-show="comparedClicks > 0"
                    >
                      {{
                        clicksChange >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down'
                      }}
                    </v-icon>
                    <v-tooltip
                      location="top"
                      :text="`Percentage change from previous period: ${clicksChange >= 0 ? '+' : '-'}${Math.abs(clicksChange).toFixed(1)}%`"
                    >
                      <template v-slot:activator="{ props }">
                        <span
                          v-bind="props"
                          :class="
                            clicksChange >= 0 ? 'text-success' : 'text-error'
                          "
                          style="cursor: help"
                        >
                          {{ Math.abs(clicksChange).toFixed(1) }}%
                        </span>
                      </template>
                    </v-tooltip>
                  </template>
                  <v-tooltip
                    location="top"
                    :text="`Previous period value: ${comparedClicks.toLocaleString()} clicks`"
                  >
                    <template v-slot:activator="{ props }">
                      <span
                        v-bind="props"
                        class="data-card__compared-value"
                        style="cursor: help"
                      >
                        ({{ comparedClicks }})
                      </span>
                    </template>
                  </v-tooltip>
                </div>
                <div v-else class="text-medium-emphasis">
                  <span>Total Number of Times Vehicles were Clicked</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Click Through Rate -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="data-card">
          <v-card-text>
            <div class="data-card__value-title">CTR</div>
            <div>
              <div
                v-if="isLoading || isWaitingForParams"
                class="data-card__value-skeleton"
              >
                <v-skeleton-loader type="chip" />
              </div>
              <div v-else class="data-card__value">{{ ctr.toFixed(2) }}%</div>
              <div
                v-if="isComparedDataLoading"
                class="data-card__stat-skeleton"
              >
                <v-skeleton-loader type="text" />
              </div>
              <div v-else class="data-card__stat">
                <div v-if="props.previousStartDate">
                  <template v-if="comparedCtr === 0">
                    <v-icon
                      v-show="ctr > 0"
                      color="success"
                      size="small"
                      class="mr-1"
                      >mdi-arrow-up</v-icon
                    >
                    <span class="text-success" v-if="ctr > 0"
                      >{{ ctr.toFixed(2) }}%</span
                    >
                    <span class="text-medium-emphasis" v-else>0.00%</span>
                  </template>
                  <template v-else>
                    <v-icon
                      :color="ctrChange >= 0 ? 'success' : 'error'"
                      size="small"
                      class="mr-1"
                      v-show="comparedCtr > 0"
                    >
                      {{ ctrChange >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                    <v-tooltip
                      location="top"
                      :text="`Percentage change from previous period: ${ctrChange >= 0 ? '+' : '-'}${Math.abs(ctrChange).toFixed(2)}%`"
                    >
                      <template v-slot:activator="{ props }">
                        <span
                          v-bind="props"
                          :class="
                            ctrChange >= 0 ? 'text-success' : 'text-error'
                          "
                          style="cursor: help"
                        >
                          {{ ctrChange.toFixed(2) }}%
                        </span>
                      </template>
                    </v-tooltip>
                  </template>
                  <v-tooltip
                    location="top"
                    :text="`Previous period value: ${comparedCtr > 0 ? comparedCtr.toFixed(2) + '%' : '0%'} CTR`"
                  >
                    <template v-slot:activator="{ props }">
                      <span
                        v-bind="props"
                        class="data-card__compared-value"
                        style="cursor: help"
                      >
                        ({{
                          comparedCtr > 0 ? comparedCtr.toFixed(2) + '%' : '0%'
                        }})
                      </span>
                    </template>
                  </v-tooltip>
                </div>
                <div v-else class="text-medium-emphasis">
                  <span>Total Click-through Rate</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card class="data-list">
          <v-card-text>
            <!-- Search Section -->
            <div class="d-flex justify-end mb-3">
              <div class="d-flex align-center gap-2">
                <v-text-field
                  v-model="searchQuery"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Search vehicles..."
                  clearable
                  hide-details="auto"
                  density="compact"
                  variant="outlined"
                  class="search-field"
                  style="width: 280px"
                  @click:clear="clearSearch"
                />
              </div>
            </div>

            <!-- Search Results Info -->
            <div
              v-if="searchQuery"
              class="text-body-2 text-medium-emphasis search-results-info mb-3"
            >
              <v-icon size="small" class="mr-1">mdi-information</v-icon>
              Showing {{ filteredAnalytics.length }} vehicles matching "{{
                searchQuery
              }}"
              <v-btn
                variant="text"
                size="small"
                color="primary"
                class="ml-2"
                @click="clearSearch"
              >
                Clear search
              </v-btn>
            </div>

            <v-data-table
              :headers="tableHeaders"
              :items="filteredAnalytics"
              :items-per-page="itemsPerPage"
              :loading="isLoading || isTableLoading"
              height="400px"
              fixed-header
              hover
              :page="page"
              :items-per-page-options="[5, 10, 20]"
              :items-length="filteredAnalytics.length"
              item-value="stockNumber"
              :custom-key-sort="customKeySort"
              :sort-by="[{ key: 'impressions', order: 'desc' }]"
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item.stockNumber }}</td>
                  <td>
                    <div class="d-flex align-center">
                      <span class="text-body-2">{{
                        getAggregatedValue(item.impressions)
                      }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <span class="text-body-2">{{
                        getAggregatedValue(item.clickCount)
                      }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <span class="text-body-2">{{
                        formatPct(item.ctrComputed ?? 0)
                      }}</span>
                    </div>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue'
import { calculatePercentageChange, formatDate } from '@/stores/utils'
import type { IVehicleAnalytic } from '@/components/dashboard/types'
import { getMustComparedAnalytics } from '@/components/dashboard/utils'
import type { IMustAnalytics } from '@/types'
import { normalizeDeviceType } from '@/utils'

const getDeviceTypeIcon = (
  deviceType: string,
): { icon: string; tooltip: string } => {
  switch (deviceType.toLowerCase()) {
    case 'desktop':
      return { icon: 'mdi-monitor', tooltip: 'Desktop' }
    case 'mobile':
      return { icon: 'mdi-cellphone', tooltip: 'Mobile' }
    case 'tablet':
      return { icon: 'mdi-tablet', tooltip: 'Tablet' }
    case 'unknown':
      return { icon: 'mdi-help-circle', tooltip: 'Unknown' }
    default:
      console.log('Falling to default case for deviceType:', deviceType)
      return { icon: 'mdi-help-circle', tooltip: 'Unknown' }
  }
}

// Helper function to create default device data for all device types
const createDefaultDeviceData = (value: string): string => {
  const deviceTypes = ['mobile', 'desktop', 'tablet', 'unknown']
  const deviceData = deviceTypes.map(deviceType => {
    const deviceIcon = getDeviceTypeIcon(deviceType)
    return `${deviceIcon.icon}:${deviceIcon.tooltip}:${value}`
  })
  return deviceData.join('|')
}

// Helper function to sort device data in the desired order
const sortDeviceData = (dataString: string): string => {
  if (!dataString || dataString === '') return ''

  const deviceOrder = ['mobile', 'desktop', 'tablet', 'unknown']
  const deviceMap = new Map()

  // Parse existing data
  dataString.split('|').forEach(item => {
    const [icon, tooltip, value] = item.split(':')
    const deviceType = tooltip.toLowerCase()
    deviceMap.set(deviceType, { icon, tooltip, value })
  })

  // Build sorted result
  const sortedDevices: string[] = []

  // First, add devices in the desired order
  deviceOrder.forEach(deviceType => {
    if (deviceMap.has(deviceType)) {
      const device = deviceMap.get(deviceType)
      sortedDevices.push(`${device.icon}:${device.tooltip}:${device.value}`)
    }
  })

  // Then, add any remaining devices that weren't in the predefined order
  deviceMap.forEach((device, deviceType) => {
    if (!deviceOrder.includes(deviceType)) {
      sortedDevices.push(`${device.icon}:${device.tooltip}:${device.value}`)
    }
  })

  return sortedDevices.join('|')
}

const parseDeviceData = (
  dataString: string,
): Array<{ icon: string; tooltip: string; value: string }> => {
  if (!dataString || dataString === '0') return []

  const deviceData = dataString
    .split('|')
    .map(item => {
      const parts = item.split(':')
      const icon = parts[0] || ''
      const tooltip = parts[1] || ''
      const value = parts[2] || ''
      return { icon, tooltip, value }
    })
    .filter(item => item.icon && item.tooltip && item.value) // Only include complete items

  // Filter by device type if filters are provided
  if (props.deviceTypeFilters && props.deviceTypeFilters.length > 0) {
    return deviceData.filter(item => {
      // Add null check for tooltip
      if (!item.tooltip) return false
      const deviceType = item.tooltip.toLowerCase()
      return props.deviceTypeFilters!.includes(deviceType)
    })
  }

  // If no device type filters are provided, return empty array (show no data)
  return []
}

// Simple helper to format CTR values with max 2 decimal places
const formatCTR = (value: string): string => {
  if (value === '0') return '0.00%'
  const numValue = parseFloat(value.replace('%', ''))
  return isNaN(numValue) ? '0.00%' : numValue.toFixed(2) + '%'
}

// Clear search function
const clearSearch = () => {
  searchQuery.value = ''
}

const getAggregatedValue = (dataString: string): string => {
  if (!dataString || dataString === '0') return '0'

  // Parse the device data string and sum up all values
  const deviceData = dataString
    .split('|')
    .map(item => {
      const parts = item.split(':')
      const value = parts[2] || '0'
      return parseFloat(value) || 0
    })
    .filter(value => !isNaN(value))

  // If device type filters are applied, only include selected device types
  if (props.deviceTypeFilters && props.deviceTypeFilters.length > 0) {
    const filteredData = dataString
      .split('|')
      .map(item => {
        const parts = item.split(':')
        const tooltip = parts[1] || ''
        const value = parts[2] || '0'
        const deviceType = tooltip.toLowerCase()

        if (props.deviceTypeFilters!.includes(deviceType)) {
          return parseFloat(value) || 0
        }
        return 0
      })
      .filter(value => !isNaN(value))

    const total = filteredData.reduce((sum, value) => sum + value, 0)
    return total.toString()
  }

  // If no device types are selected (empty array), show 0
  if (props.deviceTypeFilters && props.deviceTypeFilters.length === 0) {
    return '0'
  }

  // If no filters provided (undefined), sum all values (default behavior)
  const total = deviceData.reduce((sum, value) => sum + value, 0)
  return total.toString()
}

const props = defineProps<{
  accountId: string
  accountName: string
  placements: string[]
  startDate?: Date | null
  endDate?: Date | null
  previousStartDate?: Date | null
  previousEndDate?: Date | null
  isReady: boolean
  deviceTypeFilters?: string[]
}>()

const emit = defineEmits<{
  'update:deviceTypeFilters': [deviceType: string[]]
}>()

const toggleDeviceType = (deviceType: string) => {
  if (!props.deviceTypeFilters) {
    emit('update:deviceTypeFilters', [deviceType])
    return
  }

  const currentFilters = [...props.deviceTypeFilters]
  const index = currentFilters.indexOf(deviceType)

  if (index > -1) {
    // Remove device type if already selected
    currentFilters.splice(index, 1)
  } else {
    // Add device type if not selected
    currentFilters.push(deviceType)
  }

  emit('update:deviceTypeFilters', currentFilters)
}

interface TableHeader {
  title: string
  key: 'stockNumber' | 'impressions' | 'clickCount' | 'ctrComputed'
  align: 'start' | 'end' | 'center'
}

const tableHeaders: TableHeader[] = [
  { title: 'Stock#', key: 'stockNumber', align: 'start' },
  { title: 'Impressions', key: 'impressions', align: 'start' },
  { title: 'Clicks', key: 'clickCount', align: 'start' },
  { title: 'CTR', key: 'ctrComputed', align: 'start' },
]

const page = ref(1)
const itemsPerPage = ref(5)
const totalItems = ref(0)

const analytics = ref<IVehicleAnalytic[] | null>(null)
const searchQuery = ref('')

const sortedAnalytics = computed(() => {
  const items = analytics.value ?? []
  // Natural/alphanumeric sort: "A2" < "A10"
  return [...items].sort((a, b) =>
    String(a.stockNumber).localeCompare(String(b.stockNumber), undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  )
})

const filteredAnalytics = computed(() => {
  if (!searchQuery.value.trim()) {
    return sortedAnalytics.value
  }

  const query = searchQuery.value.toLowerCase().trim()
  return sortedAnalytics.value.filter(vehicle => {
    // Search in stock number
    if (vehicle.stockNumber.toLowerCase().includes(query)) {
      return true
    }

    // Search in aggregated impressions
    const aggregatedImpressions = getAggregatedValue(vehicle.impressions)
    if (aggregatedImpressions.toLowerCase().includes(query)) {
      return true
    }

    // Search in aggregated clicks
    const aggregatedClicks = getAggregatedValue(vehicle.clickCount)
    if (aggregatedClicks.toLowerCase().includes(query)) {
      return true
    }

    // Search in aggregated CTR
    const aggregatedCTR = formatPct(
      computeCtrFromData(vehicle.clickCount, vehicle.impressions),
    ).toLowerCase()

    if (aggregatedCTR.includes(query)) return true

    return false
  })
})

const customKeySort: Record<string, (a: any, b: any) => number> = {
  stockNumber: (a, b) =>
    String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    }),

  impressions: (a, b) =>
    parseFloat(getAggregatedValue(a)) - parseFloat(getAggregatedValue(b)),

  clickCount: (a, b) =>
    parseFloat(getAggregatedValue(a)) - parseFloat(getAggregatedValue(b)),

  ctrComputed: (a, b) => (Number(a) || 0) - (Number(b) || 0),
}

const isLoading = ref(false)
const isWaitingForParams = ref(false)
const isComparedDataLoading = ref(false)
const isTableLoading = ref(false)

// Vehicles
const totalVehicles = ref(0)
const vehiclesChange = ref(0)
const comparedVehicles = ref(0)

// Impressions
const totalImpressions = ref(0)
const impressionsChange = ref(0)
const comparedImpressions = ref(0)

// CLicks
const totalClicks = ref(0)
const clicksChange = ref(0)
const comparedClicks = ref(0)

// CTR
const ctr = ref(0)
const ctrChange = ref(0)
const comparedCtr = ref(0)

// Cache the last fetched analytics so we can recompute metrics on
// device-type filter changes without refetching from the backend
const lastCurrentData = ref<IMustAnalytics | null>(null)
const lastPreviousData = ref<IMustAnalytics | null>(null)

const getAggregatedNumber = (dataString: string): number => {
  if (!dataString || dataString === '0') return 0

  // with device-type filters
  if (props.deviceTypeFilters && props.deviceTypeFilters.length > 0) {
    return dataString.split('|').reduce((sum, item) => {
      const parts = item.split(':')
      const tooltip = (parts[1] || '').toLowerCase()
      const value = parseFloat(parts[2] || '0') || 0
      return props.deviceTypeFilters!.includes(tooltip) ? sum + value : sum
    }, 0)
  }

  // explicit empty filter -> treat as none selected
  if (props.deviceTypeFilters && props.deviceTypeFilters.length === 0) return 0

  // no filters -> sum all
  return dataString
    .split('|')
    .map(i => parseFloat(i.split(':')[2] || '0') || 0)
    .reduce((a, b) => a + b, 0)
}

const computeCtrFromData = (clicksStr: string, viewsStr: string): number => {
  const clicks = getAggregatedNumber(clicksStr)
  const views = getAggregatedNumber(viewsStr)
  if (!views) return 0
  return (clicks / views) * 100
}

const formatPct = (n: number, digits = 2): string => `${n.toFixed(digits)}%`

const handleComparedDataChange = async (
  accountId: string,
  accountName: string,
  productId: string,
  startDate: string,
  endDate: string,
) => {
  // If no placements are selected, clear all data to avoid API errors
  if (!props.placements || props.placements.length === 0) {
    // Clear all metrics
    totalVehicles.value = 0
    comparedVehicles.value = 0
    vehiclesChange.value = 0
    totalImpressions.value = 0
    comparedImpressions.value = 0
    impressionsChange.value = 0
    totalClicks.value = 0
    comparedClicks.value = 0
    clicksChange.value = 0
    ctr.value = 0
    comparedCtr.value = 0
    ctrChange.value = 0
    analytics.value = []
    totalItems.value = 0
    return
  }

  const formattedPreviousStartDate = formatDate(
    props.previousStartDate ?? new Date(),
  )
  const formattedPreviousEndDate = formatDate(
    props.previousEndDate ?? new Date(),
  )

  // Intentionally skip separate "vehicle count" request. We'll derive the
  // unique vehicle count from view/click analytics so the card matches the
  // table (vehicles with activity only).

  // Get vehicle view data
  const vehicleViewAnalytics = await getMustComparedAnalytics(
    props.accountId,
    props.accountName,
    productId,
    ['stockNumber', 'deviceType'], // Simplified dimensions
    ['_events', 'eventType', 'source', 'account', 'placement'],
    [
      {
        name: 'eventType',
        operator: '=',
        value: 'vehicle_view',
      },
    ],
    startDate,
    endDate,
    formattedPreviousStartDate,
    formattedPreviousEndDate,
    props.placements,
  )

  // Get vehicle click data
  const vehicleClickAnalytics = await getMustComparedAnalytics(
    props.accountId,
    props.accountName,
    productId,
    ['stockNumber', 'deviceType'], // Simplified dimensions
    ['_events', 'eventType', 'source', 'account', 'placement'],
    [
      {
        name: 'eventType',
        operator: '=',
        value: 'vehicle_click',
      },
      {
        name: 'stockNumber',
        operator: '<>',
        value: 'null',
      },
    ],
    startDate,
    endDate,
    formattedPreviousStartDate,
    formattedPreviousEndDate,
    props.placements,
  )

  // Compute current unique vehicles based on any activity (views or clicks)
  // to align the summary card with the table rows.
  const previousVehiclesSet = new Set<string>()

  // Create data for all vehicles shown to the user
  const vehicleDeviceMap = new Map<
    string,
    {
      views: Record<string, number>
      clicks: Record<string, number>
    }
  >()

  // Track previous-period vehicles as well (union of views and clicks)
  ;(vehicleViewAnalytics?.previous?.items ?? []).forEach(item => {
    const stockNumber = item.dimensions?.stockNumber
    if (stockNumber) previousVehiclesSet.add(stockNumber)
  })
  ;(vehicleClickAnalytics?.previous?.items ?? []).forEach(item => {
    const stockNumber = item.dimensions?.stockNumber
    if (stockNumber) previousVehiclesSet.add(stockNumber)
  })

  // Aggregate views by vehicle and deviceType
  ;(vehicleViewAnalytics?.current?.items ?? []).forEach(item => {
    const stockNumber = item.dimensions?.stockNumber
    const rawDeviceType = item.dimensions?.deviceType || 'Unknown'
    const deviceType = normalizeDeviceType(rawDeviceType)
    const viewCount = item.metrics?.['_events'] ?? 0
    if (!vehicleDeviceMap.has(stockNumber)) {
      vehicleDeviceMap.set(stockNumber, { views: {}, clicks: {} })
    }
    vehicleDeviceMap.get(stockNumber)!.views[deviceType] = viewCount
  })

  // Aggregate clicks by vehicle and deviceType
  ;(vehicleClickAnalytics?.current?.items ?? []).forEach(item => {
    const stockNumber = item.dimensions?.stockNumber
    const rawDeviceType = item.dimensions?.deviceType || 'Unknown'
    const deviceType = normalizeDeviceType(rawDeviceType)
    const clickCount = item.metrics?.['_events'] ?? 0
    if (!vehicleDeviceMap.has(stockNumber)) {
      vehicleDeviceMap.set(stockNumber, { views: {}, clicks: {} })
    }
    vehicleDeviceMap.get(stockNumber)!.clicks[deviceType] = clickCount
  })

  // Build the final data array
  const vehicleData: IVehicleAnalytic[] = []
  vehicleDeviceMap.forEach((deviceData, stockNumber) => {
    const viewStrArr: string[] = []
    const clickStrArr: string[] = []
    const ctrStrArr: string[] = []

    const allDeviceTypes = new Set([
      ...Object.keys(deviceData.views),
      ...Object.keys(deviceData.clicks),
    ])

    allDeviceTypes.forEach(deviceType => {
      const views = deviceData.views[deviceType] || 0
      const clicks = deviceData.clicks[deviceType] || 0
      const ctr = views ? ((clicks / views) * 100).toFixed(2) : '0.00'
      const deviceIcon = getDeviceTypeIcon(deviceType)

      viewStrArr.push(`${deviceIcon.icon}:${deviceIcon.tooltip}:${views}`)
      clickStrArr.push(`${deviceIcon.icon}:${deviceIcon.tooltip}:${clicks}`)
      ctrStrArr.push(`${deviceIcon.icon}:${deviceIcon.tooltip}:${ctr}%`)
    })

    vehicleData.push({
      stockNumber,
      impressions: viewStrArr.join('|'),
      clickCount: clickStrArr.join('|'),
      ctr: ctrStrArr.join('|'),
    })
  })

  // Prepare period payloads using derived unique counts so the
  // "Total Vehicles" card reflects vehicles with views/clicks only.
  const currentPeriodData: any = {
    clicks: vehicleClickAnalytics.current,
    count: vehicleDeviceMap.size,
    views: vehicleViewAnalytics.current,
  }

  const previousPeriodData: any = {
    clicks: vehicleClickAnalytics.previous,
    count: previousVehiclesSet.size,
    views: vehicleViewAnalytics.previous,
  }

  updateVehiclesData(vehicleData)
  // Cache results for recomputation when device filters change
  lastCurrentData.value = currentPeriodData
  lastPreviousData.value = previousPeriodData
  updateMetrics(currentPeriodData, previousPeriodData)
}

const updateVehiclesData = (vehiclesData: IVehicleAnalytic[] | null) => {
  totalItems.value = vehiclesData?.length ?? 0
  analytics.value =
    vehiclesData?.map((vehicle: IVehicleAnalytic) => {
      // Always start with default data (all zeros)
      let impressionsData = createDefaultDeviceData('0')
      let clickData = createDefaultDeviceData('0')
      let ctrData = createDefaultDeviceData('0%')

      // If we have analytics data, merge it with the default data
      if (vehicle.impressions) {
        impressionsData = mergeDeviceData(impressionsData, vehicle.impressions)
      }
      if (vehicle.clickCount) {
        clickData = mergeDeviceData(clickData, vehicle.clickCount)
      }
      if (vehicle.ctr) {
        ctrData = mergeDeviceData(ctrData, vehicle.ctr)
      }

      return {
        stockNumber: vehicle.stockNumber,
        impressions: impressionsData,
        clickCount: clickData,
        ctr: ctrData,
        ctrComputed: computeCtrFromData(clickData, impressionsData),
      }
    }) ?? null
}

// Helper function to merge analytics data with default data
const mergeDeviceData = (
  defaultData: string,
  analyticsData: string,
): string => {
  if (!analyticsData || analyticsData === '0') return defaultData

  const defaultMap = new Map()
  const analyticsMap = new Map()

  // Parse default data
  defaultData.split('|').forEach(item => {
    const [icon, tooltip, value] = item.split(':')
    defaultMap.set(tooltip.toLowerCase(), { icon, tooltip, value })
  })

  // Parse analytics data
  analyticsData.split('|').forEach(item => {
    const [icon, tooltip, value] = item.split(':')
    analyticsMap.set(tooltip.toLowerCase(), { icon, tooltip, value })
  })

  // Merge: use analytics data if available, otherwise use default
  const deviceTypes = ['mobile', 'desktop', 'tablet', 'unknown']
  const mergedData = deviceTypes
    .map(deviceType => {
      const analyticsItem = analyticsMap.get(deviceType)
      const defaultItem = defaultMap.get(deviceType)

      if (analyticsItem) {
        return `${analyticsItem.icon}:${analyticsItem.tooltip}:${analyticsItem.value}`
      } else if (defaultItem) {
        return `${defaultItem.icon}:${defaultItem.tooltip}:${defaultItem.value}`
      }
      return ''
    })
    .filter(item => item !== '')

  return mergedData.join('|')
}

const handlePartialDataChange = async () => {
  try {
    isComparedDataLoading.value = true

    const productId = 'micro_srp'
    const formattedStartDate = formatDate(props.startDate ?? new Date())
    const formattedEndDate = formatDate(props.endDate ?? new Date())

    await handleComparedDataChange(
      props.accountId,
      props.accountName,
      productId,
      formattedStartDate,
      formattedEndDate,
    )
  } catch (error) {
    console.error('Error fetching analytics data:', error)
  } finally {
    isComparedDataLoading.value = false
  }
}

const handleDateChange = async () => {
  try {
    isLoading.value = true
    isComparedDataLoading.value = true
    isTableLoading.value = true

    const productId = 'micro_srp'
    const formattedStartDate = formatDate(props.startDate ?? new Date())
    const formattedEndDate = formatDate(props.endDate ?? new Date())

    await handleComparedDataChange(
      props.accountId,
      props.accountName,
      productId,
      formattedStartDate,
      formattedEndDate,
    )
  } catch (error) {
    console.error('Error fetching analytics data:', error)
  } finally {
    isLoading.value = false
    isComparedDataLoading.value = false
    isTableLoading.value = false
  }
}

// Helpers to apply device type filtering to summary metrics
const shouldIncludeDeviceType = (raw: string | undefined): boolean => {
  const filters = props.deviceTypeFilters
  if (filters === undefined) return true
  if (filters.length === 0) return false
  return filters.includes(normalizeDeviceType(raw || 'unknown'))
}

const sumEvents = (items: any[] = []) =>
  items
    .filter(item => shouldIncludeDeviceType(item?.dimensions?.deviceType))
    .reduce((sum, item) => sum + (item?.metrics?.['_events'] ?? 0), 0)

const updateMetrics = (
  currentData: IMustAnalytics | null,
  previousData: IMustAnalytics | null,
) => {
  // Update vehicles
  const currentVehicleCount = currentData?.count ?? 0
  const previousVehicleCount = previousData?.count ?? 0
  totalVehicles.value = currentVehicleCount
  comparedVehicles.value = previousVehicleCount
  vehiclesChange.value = calculatePercentageChange(
    currentVehicleCount,
    previousVehicleCount,
  )

  // Update Impressions/Views
  const currentViews = sumEvents(currentData?.views?.items)
  const previousViews = sumEvents(previousData?.views?.items)
  totalImpressions.value = currentViews
  comparedImpressions.value = previousViews
  impressionsChange.value = calculatePercentageChange(
    currentViews,
    previousViews,
  )

  // Update Clicks
  const currentClicks = sumEvents(currentData?.clicks?.items)
  const previousClicks = sumEvents(previousData?.clicks?.items)
  totalClicks.value = currentClicks
  comparedClicks.value = previousClicks
  clicksChange.value = calculatePercentageChange(currentClicks, previousClicks)

  // Update CTR
  let currentCtr = currentViews === 0 ? 0 : (currentClicks / currentViews) * 100
  let previousCtr =
    previousViews === 0 ? 0 : (previousClicks / previousViews) * 100
  ctr.value = isNaN(currentCtr) ? 0 : Number(currentCtr.toFixed(2))
  comparedCtr.value = isNaN(previousCtr) ? 0 : Number(previousCtr.toFixed(2))
  ctrChange.value = calculatePercentageChange(ctr.value, comparedCtr.value)
}

onMounted(() => {
  handleDateChange()
})

watch(
  () => [props.placements],
  () => {
    handleDateChange()
  },
)

watch(() => [props.startDate, props.endDate], handleDateChange)
watch(
  () => [props.previousStartDate, props.previousEndDate],
  handlePartialDataChange,
)

// Recompute top-card metrics when device filters change (no refetch)
watch(
  () => props.deviceTypeFilters,
  () => {
    // Recompute row-level CTR so table reflects current device-type filters
    analytics.value = (analytics.value ?? []).map(row => ({
      ...row,
      ctrComputed: computeCtrFromData(row.clickCount, row.impressions),
    }))
    updateMetrics(lastCurrentData.value, lastPreviousData.value)
  },
  { deep: true },
)

watch(
  () => [props.isReady],
  ([isReady]) => {
    if (isReady) {
      isWaitingForParams.value = false
      handleDateChange()
    }
  },
)

// Reset page when search query changes
watch(searchQuery, () => {
  page.value = 1
})
</script>

<style scoped>
.data-card {
  height: 135px;
}
:deep(.v-data-table-header__content) {
  font-weight: bold !important;
}
:deep(.v-skeleton-loader__chip) {
  height: 25px;
  margin: 0 !important;
}
:deep(.v-skeleton-loader__text) {
  height: 19px;
  margin: 0 !important;
}
.data-card__value-title {
  color: #666;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.75;
}
.data-card__value {
  font-size: 2.5em;
  font-weight: bold;
  line-height: 1em;
  margin-top: 2px;
}
.data-card__compared-value {
  color: orange;
}
.data-card__value-skeleton {
  margin-top: 6px;
}
.data-card__stat {
  display: block;
  line-height: 1.25em;
  margin-top: 10px;
}
.data-card__stat-skeleton {
  margin-top: 16px;
}

/* Search section styles */
.search-field {
  transition: all 0.2s ease;
}

.search-field:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-results-chip {
  font-size: 0.75rem;
  font-weight: 500;
}

.search-results-info {
  background-color: rgba(25, 118, 210, 0.04);
  border-radius: 6px;
  padding: 8px 12px;
  border-left: 3px solid rgba(25, 118, 210, 0.2);
}
</style>
